import {
  Container,
  DivComponentEmail,
  FormSignUp,
  LoginBox,
  Logo,
  RadioContainer,
} from "./styles";
import capivara from "../../../../assets/images/capivara.png";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "../../../../components/page-base";
import { Radio } from "@mui/material";
import { toast } from "react-toastify";
import { LoginService } from "../../services";
import { TUserType } from "../../../users/models";
import permissionsStore from "../../../../store/permissions";
import { LoadingContainer } from "../main/styles";
import { useAuthContext } from "../../../../contexts";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Form from "@/components/Form";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export const SignUp: React.FC = () => {
  const { role, storageUser } = permissionsStore();
  const navigate = useNavigate();
  const { login, user } = useAuthContext();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const cardValue = urlParams.get("card");
  localStorage.setItem("puxa-capivara-card-payment", cardValue || "");

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [userType, setUserType] = useState<TUserType>("PERSON");
  const [loading, setLoading] = useState<boolean>(true);
  const [verifyEmail, setVerifyEmail] = useState<boolean>(false);
  const [emailConfirm, setEmailConf] = useState<boolean>(false);
  const [isDisabledButtonEmail, setIsDisabledButtonEmail] = useState(true);
  const [countdown, setCountdown] = useState(10);
  const [fetch, setFetch] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const schemaSignUpForm = z
    .object({
      name: z.string().nonempty("O Nome é obrigatório"),
      phone: z
        .string()
        .nonempty("O Telefone é obrigatório")
        .min(11, { message: "Informe o número completo" }),
      email: z
        .string()
        .nonempty("O email é obrigatório")
        .email("Informe um email válido")
        .transform((email) => email.toLowerCase()),
      confirmEmail: z
        .string()
        .nonempty("O email é obrigatório")
        .email("Informe um email válido")
        .transform((email) => email.toLowerCase()),
      password: z
        .string()
        .min(8, { message: "Digite uma senha com 8 ou mais caracteres" }),
      confirmPassword: z
        .string()
        .min(8, { message: "Digite uma senha com 8 ou mais caracteres" }),
      cpfCnpj: z.string().min(14, { message: "Preencha corretamente" }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: "As senhas precisam ser iguais",
      path: ["confirmPassword"],
    })
    .refine((data) => data.email === data.confirmEmail, {
      message: "Os emails precisam ser iguais",
      path: ["confirmEmail"],
    });

  type signUpFormProps = z.infer<typeof schemaSignUpForm>;

  const signUpForm = useForm<signUpFormProps>({
    resolver: zodResolver(schemaSignUpForm),
  });

  useEffect(() => {
    setIsDisabledButtonEmail(true);
    setCountdown(10);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsDisabledButtonEmail(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [emailConfirm, fetch]);

  useEffect(() => {
    if (role && storageUser && user) {
      navigate("/home");
    } else {
      setTimeout(() => {
        setLoading(false);
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleUserType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserType((event.target as HTMLInputElement).value as TUserType);
  };

  const handleSubmitLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    setFetch((prev) => !prev);
    e.preventDefault();
    setVerifyEmail(false);

    if (!executeRecaptcha) {
      // Caso a função ainda não esteja disponível
      console.error("Erro ao gerar token captcha;");
      return false;
    }

    const token = await executeRecaptcha("login");

    const loginResult = await login(email, password, token);

    if (loginResult === "Cheque sua caixa de email para confirmar a conta!") {
      setVerifyEmail(true);
    } else {
      toast.success("Seja bem vindo!");
      navigate("/credits");
    }
  };

  const handleSubmit = async (data: signUpFormProps) => {
    setLoading(true);
    setEmail(data.email);
    setPassword(data.password);

    const ambassador = urlParams.get("ambassador");

    if (!executeRecaptcha) {
      // Caso a função ainda não esteja disponível
      console.error("Erro ao gerar token captcha;");
      return false;
    }

    const token = await executeRecaptcha("ambassador");

    LoginService.signUp({
      fullName: data.name,
      phone: data.phone,
      doc: data.cpfCnpj,
      email: data.email.toLocaleLowerCase(),
      password: data.password,
      type: userType,
      token,
      ambassador,
    }).then((data) => {
      if (data.success) {
        setEmailConf(true);
      }
    });

    setLoading(false);
  };

  if (loading) {
    return <LoadingContainer>Carregando...</LoadingContainer>;
  }

  return (
    <Container className="mobile:flex-col">
      <Logo className="w-2/6 mobile:w-full mobile:mt-40">
        <img src={capivara} alt="Logo" />
      </Logo>
      <LoginBox className="w-5/6 p-4 laptop:min-w-[600px] desktop:min-w-[800px] mobile:mb-10">
        {!emailConfirm && (
          <FormProvider {...signUpForm}>
            <FormSignUp onSubmit={signUpForm.handleSubmit(handleSubmit)}>
              <Page.Button
                className="mt-1"
                type="button"
                label="Já sou cadastrado"
                onClick={() => {
                  navigate("/login");
                }}
              />
              <RadioContainer>
                <span>Eu sou:</span>
                <div
                  className="flex items-center justify-around w-full"
                  onChange={handleUserType}
                >
                  <label>
                    <Radio
                      name="user-type"
                      value="PERSON"
                      checked={userType === "PERSON"}
                    />
                    Pessoa Física
                  </label>
                  <label>
                    <Radio
                      name="user-type"
                      value="COMPANY"
                      checked={userType === "COMPANY"}
                    />
                    Pessoa Jurídica
                  </label>
                </div>
              </RadioContainer>
              <div className="flex items-center w-full mb-4 bg-gray-800 rounded-lg text-white text-[10px] text-center">
                <p className="w-3/6 px-4">
                  Como PF você recebe uma análise detalhada com critérios
                  pré-definidos.
                </p>
                <p className="w-3/6 p-4">
                  Como PJ você pode personalizar os critérios de
                  aprovação/reprovação e escolher as esferas judiciais a serem
                  analisadas: Criminal, Trabalhista, Cível e Eleitoral.
                </p>
              </div>

              <div className="flex items-center gap-1">
                {userType === "COMPANY" && (
                  <Form.Field height={54}>
                    <Form.Label>Razão social</Form.Label>
                    <Form.Input name="name" maskType="text" />
                    <Form.ErrorMessage field="name" />
                  </Form.Field>
                )}

                {userType === "PERSON" && (
                  <Form.Field height={54}>
                    <Form.Label>Nome completo</Form.Label>
                    <Form.Input name="name" maskType="text" />
                    <Form.ErrorMessage field="name" />
                  </Form.Field>
                )}
                <Form.Field height={54}>
                  <Form.Label>WhatsApp</Form.Label>
                  <Form.Input
                    name="phone"
                    placeholder="DDD 9 0000-0000"
                    maskType="tel"
                  />
                  <Form.ErrorMessage field="phone" />
                </Form.Field>
              </div>

              <div className="flex items-center gap-1">
                <Form.Field height={54}>
                  <Form.Label>Email</Form.Label>
                  <Form.Input
                    name="email"
                    placeholder="Seu melhor email"
                    maskType="email"
                  />
                  <Form.ErrorMessage field="email" />
                </Form.Field>
                <Form.Field height={54}>
                  <Form.Input
                    className="mt-5"
                    placeholder="Confirme o email"
                    name="confirmEmail"
                    maskType="email"
                  />
                  <Form.ErrorMessage field="confirmEmail" />
                </Form.Field>
              </div>

              <div className="flex items-center gap-1">
                <Form.Field height={54}>
                  <Form.Label>Senha</Form.Label>
                  <Form.Input
                    maskType="password"
                    name="password"
                    type="password"
                    placeholder="Sua melhor senha"
                  />
                  <Form.ErrorMessage field="password" />
                </Form.Field>

                <Form.Field height={54}>
                  <Form.Input
                    maskType="password"
                    className="mt-5 !pr-8"
                    placeholder="Confirme a senha"
                    name="confirmPassword"
                    type="password"
                  />
                  <Form.ErrorMessage field="confirmPassword" />
                </Form.Field>
              </div>

              {userType === "COMPANY" && (
                <Form.Field height={54}>
                  <Form.Label>CNPJ</Form.Label>
                  <Form.Input
                    className="text-center"
                    placeholder="00.000.000/0000-00"
                    name="cpfCnpj"
                    maskType="cnpj"
                  />
                  <Form.ErrorMessage field="cpfCnpj" />
                </Form.Field>
              )}

              {userType === "PERSON" && (
                <Form.Field height={54}>
                  <Form.Label>CPF</Form.Label>
                  <Form.Input
                    className="text-center"
                    placeholder="000.000.000-00"
                    name="cpfCnpj"
                    maskType="cpf"
                  />
                  <Form.ErrorMessage field="cpfCnpj" />
                </Form.Field>
              )}
              <Form.Button type="submit" className="hover:brightness-90 mt-2">
                {loading ? "Carregando..." : "Cadastrar"}
              </Form.Button>
            </FormSignUp>
          </FormProvider>
        )}
        {emailConfirm && (
          <DivComponentEmail>
            <p>
              Conta criada! <br />
              <strong>Clique no link</strong> enviado para seu email!
              <br /> Verifique sua caixa de <strong>spam</strong>
              <MarkEmailReadOutlinedIcon
                sx={{ color: "gray !important", marginLeft: "5px" }}
              />
            </p>
            <Page.Button
              type="button"
              bgColor={isDisabledButtonEmail ? "zinc" : "green"}
              disabled={isDisabledButtonEmail}
              hoverBgcolor="#008035"
              label={
                isDisabledButtonEmail
                  ? `Aguarde ${countdown}s`
                  : "JÁ CONFIRMEI MEU EMAIL"
              }
              onClick={() =>
                handleSubmitLogin({
                  preventDefault: () => {},
                } as React.FormEvent<HTMLFormElement>)
              }
            />
            {verifyEmail && (
              <div>
                <WarningAmberOutlinedIcon />
                <span>Email não verificado!</span>
              </div>
            )}
          </DivComponentEmail>
        )}
      </LoginBox>
    </Container>
  );
};
